import { useState } from 'react';
import useScrollReveal from '../useScrollReveal';
import useGsapReveal from '../useGsapReveal';
import { sectionStyles, getAnimation, hexToRgba, isColorDark, resolveLinkProps, getCardRadius, getCardPadding, getCardGap, getGridCols, getTextAlign } from '../utils';
import SectionHeader from './SectionHeader';
import SectionDivider from './SectionDivider';

export default function PricingSection({ block = {}, primaryColor, secondaryColor, enableAnimations }) {
    const { config = {}, content = {} } = block;
    const s = sectionStyles(config, primaryColor, secondaryColor);
    const [ref, visible] = useScrollReveal(0.15, !enableAnimations);
    const rawItems = content.items || [];
    const items = rawItems.length > 0 ? rawItems : [
        { name: 'Basico', price: '29', period: '/mes', features: 'Hasta 3 usuarios\nSoporte por email\n5GB almacenamiento', cta_text: 'Empezar', cta_link: '#contact' },
        { name: 'Profesional', price: '79', period: '/mes', popular: true, popular_label: 'Popular', features: 'Hasta 15 usuarios\nSoporte prioritario\n50GB almacenamiento\nAnalytics avanzado\nAPI completa', cta_text: 'Empezar', cta_link: '#contact' },
        { name: 'Enterprise', price: '199', period: '/mes', features: 'Usuarios ilimitados\nSoporte 24/7 dedicado\n1TB almacenamiento\nSSO y SAML\nSLA 99.99%', cta_text: 'Contactar', cta_link: '#contact' },
    ];
    const anim = getAnimation(config, visible);
    const [isAnnual, setIsAnnual] = useState(false);
    const gsapRef = useGsapReveal({ animation: 'scale', disabled: !enableAnimations });

    const variant = config.variant || 'cards';
    const cls = `${s.padClass} relative ${s.radiusClass} ${s.shadowClass}`;
    const bgS = s.bgStyle.background ? s.bgStyle : { backgroundColor: s.bg || '#ffffff', ...s.bgStyle };

    // Check if any plan has annual pricing
    const hasAnnualPricing = items.some(p => p.price_annual);
    const showToggle = config.show_toggle && hasAnnualPricing;

    // Find a discount label from any plan
    const discountLabel = items.find(p => p.discount_label)?.discount_label;

    // Shared toggle component
    const Toggle = () => showToggle ? (
        <div className={`flex items-center justify-center gap-3 mb-8 transition-all duration-500 ${visible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-4'}`}>
            <div className="inline-flex items-center rounded-full p-1" style={{ backgroundColor: s.dark ? 'rgba(255,255,255,0.1)' : '#f3f4f6' }}>
                <button onClick={() => setIsAnnual(false)} className={`px-5 py-2 rounded-full text-sm font-semibold transition-all duration-300 ${!isAnnual ? 'text-white shadow-md' : s.dark ? 'text-white/60 hover:text-white/80' : ''}`}
                    style={!isAnnual ? { backgroundColor: s.accent } : { color: s.textSub }}>{content.monthly_label || 'Mensual'}</button>
                <button onClick={() => setIsAnnual(true)} className={`px-5 py-2 rounded-full text-sm font-semibold transition-all duration-300 ${isAnnual ? 'text-white shadow-md' : s.dark ? 'text-white/60 hover:text-white/80' : ''}`}
                    style={isAnnual ? { backgroundColor: s.accent } : { color: s.textSub }}>{content.annual_label || 'Anual'}</button>
            </div>
            {isAnnual && discountLabel && (
                <span className="inline-flex items-center gap-1 px-3 py-1 rounded-full text-xs font-bold text-white animate-pulse" style={{ backgroundColor: s.accent }}>{discountLabel}</span>
            )}
        </div>
    ) : null;

    // Shared price display
    const PriceDisplay = ({ plan, colorStyle }) => {
        const displayPrice = isAnnual && plan.price_annual ? plan.price_annual : plan.price;
        const priceChanged = isAnnual && plan.price_annual && plan.price_annual !== plan.price;
        return (
            <div className="mt-4 mb-6">
                {plan.original_price && plan.original_price !== displayPrice && (
                    <span className="text-lg line-through opacity-40 mr-2">{plan.currency || 'S/'} {plan.original_price}</span>
                )}
                <span className="text-4xl font-extrabold transition-all duration-500" style={{ ...colorStyle, transform: priceChanged ? 'scale(1.05)' : 'scale(1)', display: 'inline-block' }}>
                    {plan.currency || 'S/'} {displayPrice}
                </span>
                {plan.period && <span className="text-sm ml-1" style={{ color: s.textSub }}>{isAnnual && plan.price_annual ? (plan.annual_period || '/anual') : plan.period}</span>}
                {priceChanged && plan.discount_label && (
                    <span className="ml-2 inline-flex items-center px-2 py-0.5 rounded-full text-[10px] font-bold text-white" style={{ backgroundColor: s.accent }}>{plan.discount_label}</span>
                )}
            </div>
        );
    };

    // Shared features list
    const FeatureList = ({ plan, checkColor }) => {
        const features = Array.isArray(plan.features) ? plan.features : (plan.features || '').split('\n').filter(f => f.trim());
        if (features.length === 0) return null;
        return (
            <ul className="space-y-3 flex-1 mb-6">
                {features.map((f, fi) => (
                    <li key={fi} className="flex items-start gap-2 text-sm" style={{ color: s.textSub }}>
                        <svg className="w-4 h-4 shrink-0 mt-0.5" style={{ color: checkColor || s.accent }} fill="none" viewBox="0 0 24 24" stroke="currentColor">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M5 13l4 4L19 7"/>
                        </svg>
                        {f.trim()}
                    </li>
                ))}
            </ul>
        );
    };

    const defaultGridCols = items.length === 2 ? 'md:grid-cols-2 max-w-3xl' : items.length >= 3 ? 'md:grid-cols-3 max-w-5xl' : 'max-w-sm';
    const gridCls = `grid grid-cols-1 ${getGridCols(config) || defaultGridCols} mx-auto ${getCardGap(config) || 'gap-6'}`;

    // ── Flat variant ──
    if (variant === 'flat') {
        return (
            <section className={cls} style={bgS}>
                <SectionDivider type={config.divider_top} color="transparent" position="top"  config={config}/>
                <div ref={ref} className={`${s.maxWidth} mx-auto px-4 sm:px-6 lg:px-8 ${anim}`}>
                    <SectionHeader tag={content.tag} title={content.title} subtitle={content.subtitle} config={config} s={s} />
                    <Toggle />
                    <div className={gridCls}>
                        {items.map((plan, i) => {
                            const isPopular = plan.popular;
                            return (
                                <div key={i} className={`relative ${getCardRadius(config) || 'rounded-xl'} ${getCardPadding(config) || 'p-6'} flex flex-col transition-all duration-500 hover:-translate-y-1 ${visible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-6'} ${
                                    isPopular ? 'border-2' : 'border'
                                }`}
                                    style={{
                                        backgroundColor: s.dark ? 'rgba(255,255,255,0.03)' : '#ffffff',
                                        borderColor: isPopular ? s.accent : (s.dark ? 'rgba(255,255,255,0.1)' : '#e5e7eb'),
                                        transitionDelay: visible ? `${i * 100}ms` : '0ms',
                                    }}>
                                    {isPopular && (
                                        <span className="absolute -top-3 left-4 px-3 py-0.5 rounded-full text-[11px] font-bold text-white" style={{ backgroundColor: s.accent }}>
                                            {plan.popular_label || 'Popular'}
                                        </span>
                                    )}
                                    <h3 className="text-lg font-bold" style={{ color: s.text }}>{plan.name}</h3>
                                    <PriceDisplay plan={plan} colorStyle={{ color: s.text }} />
                                    <FeatureList plan={plan} />
                                    {plan.cta_text && (
                                        <a {...resolveLinkProps(plan.cta_link || '#contact')}
                                            className={`block w-full text-center py-3 rounded-lg font-semibold text-sm transition-all hover:opacity-90 ${isPopular ? 'text-white' : ''}`}
                                            style={isPopular
                                                ? { backgroundColor: plan.button_color || s.accent }
                                                : { backgroundColor: s.dark ? 'rgba(255,255,255,0.08)' : '#f3f4f6', color: s.text }}>
                                            {plan.cta_text}
                                        </a>
                                    )}
                                </div>
                            );
                        })}
                    </div>
                </div>
                <SectionDivider type={config.divider_bottom} color={config.divider_bottom_color || '#ffffff'} position="bottom"  config={config}/>
            </section>
        );
    }

    // ── Gradient variant ──
    if (variant === 'gradient') {
        return (
            <section className={cls} style={bgS}>
                <SectionDivider type={config.divider_top} color="transparent" position="top"  config={config}/>
                <div ref={ref} className={`${s.maxWidth} mx-auto px-4 sm:px-6 lg:px-8 ${anim}`}>
                    <SectionHeader tag={content.tag} title={content.title} subtitle={content.subtitle} config={config} s={s} />
                    <Toggle />
                    <div className={gridCls}>
                        {items.map((plan, i) => {
                            const isPopular = plan.popular;
                            const cardBg = isPopular
                                ? `linear-gradient(135deg, ${s.accent}, ${hexToRgba(s.accent, 0.7)})`
                                : `linear-gradient(135deg, ${hexToRgba(s.accent, 0.15)}, transparent)`;
                            const textColor = isPopular ? (isColorDark(s.accent) ? '#ffffff' : '#111827') : s.text;
                            return (
                                <div key={i} className={`relative ${getCardRadius(config) || 'rounded-2xl'} ${getCardPadding(config) || 'p-6'} flex flex-col shadow-lg transition-all duration-500 hover:-translate-y-1 ${visible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-6'} ${
                                    isPopular ? 'scale-[1.02] shadow-xl' : ''
                                }`}
                                    style={{
                                        background: cardBg,
                                        transitionDelay: visible ? `${i * 100}ms` : '0ms',
                                    }}>
                                    {isPopular && (
                                        <span className="absolute -top-3 left-1/2 -translate-x-1/2 px-4 py-1 rounded-full text-xs font-bold text-white shadow-md" style={{ backgroundColor: s.secondary || s.accent }}>
                                            {plan.popular_label || 'Popular'}
                                        </span>
                                    )}
                                    <h3 className="text-lg font-bold" style={{ color: textColor }}>{plan.name}</h3>
                                    <PriceDisplay plan={plan} colorStyle={{ color: textColor }} />
                                    <FeatureList plan={plan} checkColor={isPopular ? textColor : s.accent} />
                                    {plan.cta_text && (
                                        <a {...resolveLinkProps(plan.cta_link || '#contact')}
                                            className="block w-full text-center py-3 rounded-xl font-semibold text-sm transition-all hover:shadow-lg"
                                            style={isPopular
                                                ? { backgroundColor: '#ffffff', color: s.accent }
                                                : { backgroundColor: s.accent, color: '#ffffff' }}>
                                            {plan.cta_text}
                                        </a>
                                    )}
                                </div>
                            );
                        })}
                    </div>
                </div>
                <SectionDivider type={config.divider_bottom} color={config.divider_bottom_color || '#ffffff'} position="bottom"  config={config}/>
            </section>
        );
    }

    // ── Glassmorphism variant ──
    if (variant === 'glassmorphism') {
        return (
            <section className={cls} style={{ ...bgS, backgroundColor: config.bg_color || '#0f172a' }}>
                <SectionDivider type={config.divider_top} color="transparent" position="top" config={config}/>
                <div ref={ref} className={`${s.maxWidth} mx-auto px-4 sm:px-6 lg:px-8 ${anim}`}>
                    <SectionHeader tag={content.tag} title={content.title} subtitle={content.subtitle} config={config} s={{...s, text: '#ffffff', textSub: 'rgba(255,255,255,0.6)', dark: true}} />
                    <Toggle />
                    <div className={gridCls}>
                        {items.map((plan, i) => {
                            const isPopular = plan.popular;
                            const displayPrice = isAnnual && plan.price_annual ? plan.price_annual : plan.price;
                            const priceChanged = isAnnual && plan.price_annual && plan.price_annual !== plan.price;
                            const features = Array.isArray(plan.features) ? plan.features : (plan.features || '').split('\n').filter(f => f.trim());
                            return (
                                <div key={i} className={`relative flex flex-col backdrop-blur-xl ${getCardRadius(config) || 'rounded-3xl'} ${getCardPadding(config) || 'p-8'} transition-all duration-500 hover:-translate-y-1 ${visible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-6'} ${
                                    isPopular ? 'scale-105 ring-2' : ''
                                }`}
                                    style={{
                                        backgroundColor: isPopular ? 'rgba(255,255,255,0.1)' : 'rgba(255,255,255,0.05)',
                                        border: `1px solid ${isPopular ? 'rgba(255,255,255,0.2)' : 'rgba(255,255,255,0.1)'}`,
                                        ringColor: isPopular ? s.accent : undefined,
                                        '--tw-ring-color': isPopular ? s.accent : undefined,
                                        transitionDelay: visible ? `${i * 100}ms` : '0ms',
                                    }}>
                                    {isPopular && (
                                        <span className="absolute -top-3.5 left-1/2 -translate-x-1/2 px-5 py-1.5 rounded-full text-xs font-bold text-white shadow-lg"
                                            style={{ backgroundColor: s.accent, boxShadow: `0 0 20px ${hexToRgba(s.accent, 0.4)}` }}>
                                            {plan.popular_label || 'Mas popular'}
                                        </span>
                                    )}
                                    <h3 className="text-lg font-bold text-white">{plan.name}</h3>
                                    {plan.description && <p className="text-sm mt-1" style={{ color: 'rgba(255,255,255,0.5)' }}>{plan.description}</p>}
                                    <div className="mt-4 mb-6">
                                        {plan.original_price && plan.original_price !== displayPrice && (
                                            <span className="text-lg line-through mr-2" style={{ color: 'rgba(255,255,255,0.3)' }}>{plan.currency || 'S/'} {plan.original_price}</span>
                                        )}
                                        <span className="text-4xl font-extrabold text-white transition-all duration-500" style={{ transform: priceChanged ? 'scale(1.05)' : 'scale(1)', display: 'inline-block' }}>
                                            <span style={{ color: s.accent }}>{plan.currency || 'S/'}</span> {displayPrice}
                                        </span>
                                        {plan.period && <span className="text-sm ml-1" style={{ color: 'rgba(255,255,255,0.4)' }}>{isAnnual && plan.price_annual ? (plan.annual_period || '/anual') : plan.period}</span>}
                                        {priceChanged && plan.discount_label && (
                                            <span className="ml-2 inline-flex items-center px-2 py-0.5 rounded-full text-[10px] font-bold text-white" style={{ backgroundColor: s.accent }}>{plan.discount_label}</span>
                                        )}
                                    </div>
                                    {features.length > 0 && (
                                        <ul className="space-y-3 flex-1 mb-6">
                                            {features.map((f, fi) => (
                                                <li key={fi} className="flex items-start gap-2 text-sm text-white/70">
                                                    <svg className="w-4 h-4 shrink-0 mt-0.5" style={{ color: s.accent }} fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M5 13l4 4L19 7"/>
                                                    </svg>
                                                    {typeof f === 'string' ? f.trim() : f}
                                                </li>
                                            ))}
                                        </ul>
                                    )}
                                    {plan.cta_text && (
                                        <a {...resolveLinkProps(plan.cta_link || '#contact')}
                                            className={`block w-full text-center py-3 rounded-xl font-semibold text-sm transition-all hover:opacity-90 ${isPopular ? '' : 'border'}`}
                                            style={isPopular
                                                ? { backgroundColor: s.accent, color: isColorDark(s.accent) ? '#ffffff' : '#111827' }
                                                : { borderColor: 'rgba(255,255,255,0.3)', color: '#ffffff', backgroundColor: 'rgba(255,255,255,0.05)' }}>
                                            {plan.cta_text}
                                        </a>
                                    )}
                                </div>
                            );
                        })}
                    </div>
                </div>
                <SectionDivider type={config.divider_bottom} color={config.divider_bottom_color || '#ffffff'} position="bottom" config={config}/>
            </section>
        );
    }

    // ── Toggle variant ──
    if (variant === 'toggle') {
        return (
            <section className={cls} style={bgS}>
                <SectionDivider type={config.divider_top} color="transparent" position="top" config={config}/>
                <div ref={ref} className={`${s.maxWidth} mx-auto px-4 sm:px-6 lg:px-8 ${anim}`}>
                    <SectionHeader tag={content.tag} title={content.title} subtitle={content.subtitle} config={config} s={s} />
                    {/* Animated toggle switch */}
                    <div className={`flex items-center justify-center gap-4 mb-10 transition-all duration-500 ${visible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-4'}`}>
                        <span className={`text-sm font-semibold transition-colors duration-300 ${!isAnnual ? 'opacity-100' : 'opacity-50'}`} style={{ color: s.text }}>{content.monthly_label || 'Mensual'}</span>
                        <button
                            onClick={() => setIsAnnual(!isAnnual)}
                            className="relative w-14 h-7 rounded-full transition-colors duration-300 focus:outline-none"
                            style={{ backgroundColor: isAnnual ? s.accent : (s.dark ? 'rgba(255,255,255,0.2)' : '#d1d5db') }}
                            aria-label="Toggle billing period"
                        >
                            <span className="absolute top-0.5 left-0.5 w-6 h-6 bg-white rounded-full shadow-md transition-transform duration-300"
                                style={{ transform: isAnnual ? 'translateX(28px)' : 'translateX(0)' }} />
                        </button>
                        <span className={`text-sm font-semibold transition-colors duration-300 ${isAnnual ? 'opacity-100' : 'opacity-50'}`} style={{ color: s.text }}>
                            {content.annual_label || 'Anual'}
                        </span>
                        {isAnnual && (
                            <span className="inline-flex items-center px-3 py-1 rounded-full text-xs font-bold text-white animate-bounce" style={{ backgroundColor: s.accent }}>
                                {discountLabel || 'Ahorra 20%'}
                            </span>
                        )}
                    </div>
                    <div className={gridCls}>
                        {items.map((plan, i) => {
                            const isPopular = plan.popular;
                            const monthlyPrice = plan.price;
                            const annualPrice = plan.annual_price || plan.price_annual || (parseFloat(String(plan.price).replace(/[^0-9.]/g, '')) * 10 || plan.price);
                            const displayPrice = isAnnual ? annualPrice : monthlyPrice;
                            const features = Array.isArray(plan.features) ? plan.features : (plan.features || '').split('\n').filter(f => f.trim());
                            return (
                                <div key={i} className={`relative rounded-2xl p-6 flex flex-col transition-all duration-500 hover:-translate-y-1 ${visible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-6'} ${
                                    isPopular ? 'shadow-2xl -translate-y-2' : 'shadow-md'
                                }`}
                                    style={{
                                        backgroundColor: s.dark ? (isPopular ? 'rgba(255,255,255,0.08)' : 'rgba(255,255,255,0.03)') : '#ffffff',
                                        border: `${isPopular ? '2px' : '1px'} solid ${isPopular ? s.accent : (s.dark ? 'rgba(255,255,255,0.1)' : '#e5e7eb')}`,
                                        transitionDelay: visible ? `${i * 100}ms` : '0ms',
                                    }}>
                                    {isPopular && (
                                        <span className="absolute -top-3 left-1/2 -translate-x-1/2 px-4 py-1 rounded-full text-xs font-bold text-white" style={{ backgroundColor: s.accent }}>
                                            {plan.popular_label || 'Recomendado'}
                                        </span>
                                    )}
                                    <h3 className="text-lg font-bold" style={{ color: s.text }}>{plan.name}</h3>
                                    {plan.description && <p className="text-sm mt-1" style={{ color: s.textSub }}>{plan.description}</p>}
                                    <div className="mt-4 mb-6 relative overflow-hidden">
                                        <div className="transition-all duration-500 ease-out">
                                            <span className="text-4xl font-extrabold transition-all duration-500" style={{ color: isPopular ? s.accent : s.text, display: 'inline-block', transform: `scale(${isAnnual ? 1.05 : 1})` }}>
                                                {plan.currency || 'S/'} {displayPrice}
                                            </span>
                                            <span className="text-sm ml-1" style={{ color: s.textSub }}>{isAnnual ? (plan.annual_period || '/anual') : (plan.period || '/mes')}</span>
                                        </div>
                                        {isAnnual && (
                                            <span className="inline-flex items-center mt-1 px-2 py-0.5 rounded-full text-[10px] font-bold text-white" style={{ backgroundColor: s.accent }}>
                                                {plan.discount_label || 'Ahorra 20%'}
                                            </span>
                                        )}
                                    </div>
                                    {features.length > 0 && (
                                        <ul className="space-y-3 flex-1 mb-6">
                                            {features.map((f, fi) => (
                                                <li key={fi} className="flex items-start gap-2 text-sm" style={{ color: s.textSub }}>
                                                    <svg className="w-4 h-4 shrink-0 mt-0.5" style={{ color: s.accent }} fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M5 13l4 4L19 7"/>
                                                    </svg>
                                                    {typeof f === 'string' ? f.trim() : f}
                                                </li>
                                            ))}
                                        </ul>
                                    )}
                                    {plan.cta_text && (
                                        <a {...resolveLinkProps(plan.cta_link || '#contact')}
                                            className={`block w-full text-center py-3 rounded-xl font-semibold text-sm transition-all hover:shadow-lg ${isPopular ? 'text-white' : ''}`}
                                            style={isPopular
                                                ? { backgroundColor: s.accent, color: isColorDark(s.accent) ? '#ffffff' : '#111827' }
                                                : { backgroundColor: hexToRgba(s.accent, 0.1), color: s.accent }}>
                                            {plan.cta_text}
                                        </a>
                                    )}
                                </div>
                            );
                        })}
                    </div>
                </div>
                <SectionDivider type={config.divider_bottom} color={config.divider_bottom_color || '#ffffff'} position="bottom" config={config}/>
            </section>
        );
    }

    // ── Comparison variant ──
    if (variant === 'comparison') {
        // Collect all unique features across plans
        const allFeatures = [];
        const featureSet = new Set();
        items.forEach(plan => {
            const features = Array.isArray(plan.features) ? plan.features : (plan.features || '').split('\n').filter(f => f.trim());
            features.forEach(f => {
                const label = typeof f === 'object' ? (f.label || f.text || '') : (typeof f === 'string' ? f.trim() : '');
                if (label && !featureSet.has(label)) {
                    featureSet.add(label);
                    allFeatures.push(label);
                }
            });
        });

        // Check if a plan has a specific feature
        const planHasFeature = (plan, featureLabel) => {
            const features = Array.isArray(plan.features) ? plan.features : (plan.features || '').split('\n').filter(f => f.trim());
            return features.some(f => {
                const label = typeof f === 'object' ? (f.label || f.text || '') : (typeof f === 'string' ? f.trim() : '');
                return label === featureLabel;
            });
        };

        return (
            <section className={cls} style={bgS}>
                <SectionDivider type={config.divider_top} color="transparent" position="top" config={config}/>
                <div ref={ref} className={`${s.maxWidth} mx-auto px-4 sm:px-6 lg:px-8 ${anim}`}>
                    <SectionHeader tag={content.tag} title={content.title} subtitle={content.subtitle} config={config} s={s} />
                    <Toggle />
                    <div className={`overflow-x-auto -mx-4 sm:mx-0 transition-all duration-500 ${visible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-6'}`}>
                        <table className="w-full min-w-[600px] border-collapse">
                            <thead>
                                <tr className="sticky top-0 z-10" style={{ backgroundColor: s.dark ? '#0f172a' : (config.bg_color || '#ffffff') }}>
                                    <th className="text-left p-4 font-semibold text-sm sticky left-0 z-20 min-w-[160px]"
                                        style={{ color: s.textSub, backgroundColor: s.dark ? '#0f172a' : (config.bg_color || '#ffffff') }}>
                                        {content.features_label || 'Caracteristicas'}
                                    </th>
                                    {items.map((plan, i) => {
                                        const isPopular = plan.popular;
                                        const displayPrice = isAnnual && plan.price_annual ? plan.price_annual : plan.price;
                                        return (
                                            <th key={i} className={`text-center p-4 min-w-[140px] ${isPopular ? 'relative' : ''}`}
                                                style={isPopular ? { backgroundColor: hexToRgba(s.accent, 0.08) } : {}}>
                                                {isPopular && (
                                                    <span className="absolute -top-3 left-1/2 -translate-x-1/2 px-3 py-0.5 rounded-full text-[10px] font-bold text-white whitespace-nowrap" style={{ backgroundColor: s.accent }}>
                                                        {plan.popular_label || 'Mas popular'}
                                                    </span>
                                                )}
                                                <div className="text-base font-bold" style={{ color: s.text }}>{plan.name}</div>
                                                <div className="text-2xl font-extrabold mt-1" style={{ color: isPopular ? s.accent : s.text }}>
                                                    {plan.currency || 'S/'} {displayPrice}
                                                </div>
                                                {plan.period && <div className="text-xs mt-0.5" style={{ color: s.textSub }}>{isAnnual && plan.price_annual ? (plan.annual_period || '/anual') : plan.period}</div>}
                                            </th>
                                        );
                                    })}
                                </tr>
                            </thead>
                            <tbody>
                                {allFeatures.map((feature, fi) => (
                                    <tr key={fi} className="transition-colors" style={{ borderBottom: `1px solid ${s.dark ? 'rgba(255,255,255,0.06)' : '#f3f4f6'}` }}>
                                        <td className="p-4 text-sm font-medium sticky left-0 min-w-[160px]"
                                            style={{ color: s.text, backgroundColor: s.dark ? '#0f172a' : (config.bg_color || '#ffffff') }}>
                                            {feature}
                                        </td>
                                        {items.map((plan, pi) => {
                                            const has = planHasFeature(plan, feature);
                                            const isPopular = plan.popular;
                                            return (
                                                <td key={pi} className="p-4 text-center"
                                                    style={isPopular ? { backgroundColor: hexToRgba(s.accent, 0.04) } : {}}>
                                                    {has ? (
                                                        <svg className="w-5 h-5 mx-auto" style={{ color: s.accent }} fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M5 13l4 4L19 7"/>
                                                        </svg>
                                                    ) : (
                                                        <svg className="w-5 h-5 mx-auto opacity-30" style={{ color: s.dark ? 'rgba(255,255,255,0.3)' : '#9ca3af' }} fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12"/>
                                                        </svg>
                                                    )}
                                                </td>
                                            );
                                        })}
                                    </tr>
                                ))}
                            </tbody>
                            <tfoot>
                                <tr>
                                    <td className="p-4 sticky left-0" style={{ backgroundColor: s.dark ? '#0f172a' : (config.bg_color || '#ffffff') }}></td>
                                    {items.map((plan, i) => {
                                        const isPopular = plan.popular;
                                        return (
                                            <td key={i} className="p-4 text-center" style={isPopular ? { backgroundColor: hexToRgba(s.accent, 0.04) } : {}}>
                                                {plan.cta_text && (
                                                    <a {...resolveLinkProps(plan.cta_link || '#contact')}
                                                        className={`inline-block px-6 py-2.5 rounded-xl font-semibold text-sm transition-all hover:shadow-lg ${isPopular ? 'text-white' : ''}`}
                                                        style={isPopular
                                                            ? { backgroundColor: s.accent, color: isColorDark(s.accent) ? '#ffffff' : '#111827' }
                                                            : { border: `2px solid ${s.accent}`, color: s.accent }}>
                                                        {plan.cta_text}
                                                    </a>
                                                )}
                                            </td>
                                        );
                                    })}
                                </tr>
                            </tfoot>
                        </table>
                    </div>
                </div>
                <SectionDivider type={config.divider_bottom} color={config.divider_bottom_color || '#ffffff'} position="bottom" config={config}/>
            </section>
        );
    }

    // ── Accordion variant ──
    if (variant === 'accordion') {
        const [openIdx, setOpenIdx] = useState(items.findIndex(p => p.popular) >= 0 ? items.findIndex(p => p.popular) : 0);
        return (
            <section className={cls} style={bgS}>
                <SectionDivider type={config.divider_top} color="transparent" position="top" config={config}/>
                <div ref={ref} className={`max-w-2xl mx-auto px-4 sm:px-6 lg:px-8 ${anim}`}>
                    <SectionHeader tag={content.tag} title={content.title} subtitle={content.subtitle} config={config} s={s} />
                    <Toggle />
                    <div className="space-y-3 mt-8">
                        {items.map((plan, i) => {
                            const isOpen = openIdx === i;
                            const isPopular = plan.popular;
                            return (
                                <div key={i} className={`rounded-2xl border-2 overflow-hidden transition-all duration-300 ${isOpen ? '' : 'cursor-pointer hover:shadow-md'}`}
                                    style={{ borderColor: isPopular ? s.accent : (s.dark ? 'rgba(255,255,255,0.1)' : '#e5e7eb'), backgroundColor: s.dark ? 'rgba(255,255,255,0.03)' : '#fff' }}
                                    onClick={() => !isOpen && setOpenIdx(i)}>
                                    <div className="flex items-center justify-between p-5">
                                        <div className="flex items-center gap-3">
                                            <h3 className={`text-lg font-bold ${s.dark ? 'text-white' : 'text-gray-900'}`}>{plan.name}</h3>
                                            {isPopular && <span className="text-[10px] font-bold uppercase px-2 py-0.5 rounded-full text-white" style={{ backgroundColor: s.accent }}>{plan.popular_label || 'Popular'}</span>}
                                        </div>
                                        <div className="flex items-center gap-3">
                                            <span className={`text-2xl font-extrabold ${s.dark ? 'text-white' : 'text-gray-900'}`}>{plan.currency || 'S/'} {isAnnual && plan.price_annual ? plan.price_annual : plan.price}<span className="text-sm font-normal ml-1" style={{ color: s.textSub }}>{plan.period}</span></span>
                                            <svg className={`w-5 h-5 transition-transform duration-300 ${isOpen ? 'rotate-180' : ''}`} style={{ color: s.textSub }} fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7"/></svg>
                                        </div>
                                    </div>
                                    <div className={`transition-all duration-300 overflow-hidden ${isOpen ? 'max-h-[600px] opacity-100' : 'max-h-0 opacity-0'}`}>
                                        <div className="px-5 pb-5 pt-2 border-t" style={{ borderColor: s.dark ? 'rgba(255,255,255,0.1)' : '#f3f4f6' }}>
                                            {plan.description && <p className="text-sm mb-4" style={{ color: s.textSub }}>{plan.description}</p>}
                                            <FeatureList plan={plan} checkColor={s.accent} />
                                            {plan.cta_text && <a {...resolveLinkProps(plan.cta_link || '#contact')} className="inline-flex items-center px-6 py-3 rounded-xl text-sm font-semibold text-white hover:opacity-90 transition-all" style={{ backgroundColor: s.accent }}>{plan.cta_text}</a>}
                                        </div>
                                    </div>
                                </div>
                            );
                        })}
                    </div>
                </div>
                <SectionDivider type={config.divider_bottom} color={config.divider_bottom_color || '#ffffff'} position="bottom" config={config}/>
            </section>
        );
    }

    // ── Single Spotlight variant ──
    if (variant === 'single-spotlight') {
        const mainPlan = items.find(p => p.popular) || items[0];
        const otherPlans = items.filter(p => p !== mainPlan);
        return (
            <section className={cls} style={bgS}>
                <SectionDivider type={config.divider_top} color="transparent" position="top" config={config}/>
                <div ref={ref} className={`max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 text-center ${anim}`}>
                    <SectionHeader tag={content.tag} title={content.title} subtitle={content.subtitle} config={config} s={s} />
                    <Toggle />
                    {/* Main plan — hero-sized */}
                    <div className="mt-8 rounded-3xl p-8 sm:p-12 border-2 shadow-xl" style={{ borderColor: s.accent, backgroundColor: s.dark ? 'rgba(255,255,255,0.05)' : '#fff' }}>
                        {mainPlan.popular_label && <span className="inline-block text-xs font-bold uppercase px-3 py-1 rounded-full text-white mb-4" style={{ backgroundColor: s.accent }}>{mainPlan.popular_label || 'Recomendado'}</span>}
                        <h3 className={`text-2xl font-bold ${s.dark ? 'text-white' : 'text-gray-900'}`}>{mainPlan.name}</h3>
                        <div className="mt-4 mb-6">
                            <span className="text-6xl font-extrabold" style={{ color: s.accent }}>{mainPlan.currency || 'S/'} {isAnnual && mainPlan.price_annual ? mainPlan.price_annual : mainPlan.price}</span>
                            {mainPlan.period && <span className="text-lg ml-2" style={{ color: s.textSub }}>{mainPlan.period}</span>}
                        </div>
                        <div className="grid sm:grid-cols-2 gap-x-8 gap-y-2 text-left max-w-lg mx-auto mb-8">
                            <FeatureList plan={mainPlan} checkColor={s.accent} />
                        </div>
                        {mainPlan.cta_text && <a {...resolveLinkProps(mainPlan.cta_link || '#contact')} className="inline-flex items-center px-10 py-4 rounded-xl text-base font-semibold text-white shadow-lg hover:shadow-xl hover:scale-105 transition-all" style={{ backgroundColor: s.accent }}>{mainPlan.cta_text}</a>}
                    </div>
                    {/* Other plans — compact strip */}
                    {otherPlans.length > 0 && (
                        <div className={`mt-6 grid ${otherPlans.length === 1 ? 'grid-cols-1 max-w-sm' : 'grid-cols-2 max-w-xl'} mx-auto gap-4`}>
                            {otherPlans.map((plan, i) => (
                                <div key={i} className={`rounded-xl p-4 border text-left ${s.dark ? 'border-white/10 bg-white/5' : 'border-gray-200 bg-gray-50'}`}>
                                    <div className="flex items-center justify-between">
                                        <span className={`font-semibold ${s.dark ? 'text-white' : 'text-gray-800'}`}>{plan.name}</span>
                                        <span className="font-bold" style={{ color: s.accent }}>{plan.currency || 'S/'} {isAnnual && plan.price_annual ? plan.price_annual : plan.price}{plan.period && <span className="text-xs font-normal ml-0.5" style={{ color: s.textSub }}>{plan.period}</span>}</span>
                                    </div>
                                    {plan.cta_text && <a {...resolveLinkProps(plan.cta_link || '#contact')} className="mt-2 inline-flex text-xs font-semibold hover:opacity-80 transition-opacity" style={{ color: s.accent }}>{plan.cta_text} →</a>}
                                </div>
                            ))}
                        </div>
                    )}
                </div>
                <SectionDivider type={config.divider_bottom} color={config.divider_bottom_color || '#ffffff'} position="bottom" config={config}/>
            </section>
        );
    }

    // ── Split Detail variant (master-detail) ──
    if (variant === 'split-detail') {
        const [selectedIdx, setSelectedIdx] = useState(items.findIndex(p => p.popular) >= 0 ? items.findIndex(p => p.popular) : 0);
        const selected = items[selectedIdx] || items[0];
        return (
            <section className={cls} style={bgS}>
                <SectionDivider type={config.divider_top} color="transparent" position="top" config={config}/>
                <div ref={ref} className={`max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 ${anim}`}>
                    <SectionHeader tag={content.tag} title={content.title} subtitle={content.subtitle} config={config} s={s} />
                    <Toggle />
                    <div className="mt-8 grid md:grid-cols-[220px_1fr] gap-4">
                        {/* Left sidebar nav */}
                        <div className="space-y-2">
                            {items.map((plan, i) => (
                                <button key={i} onClick={() => setSelectedIdx(i)}
                                    className={`w-full text-left px-4 py-3 rounded-xl text-sm font-medium transition-all ${selectedIdx === i
                                        ? (s.dark ? 'bg-white/15 text-white' : 'text-gray-900 shadow-md')
                                        : (s.dark ? 'text-white/60 hover:text-white hover:bg-white/5' : 'text-gray-500 hover:text-gray-700 hover:bg-gray-50')
                                    }`}
                                    style={selectedIdx === i && !s.dark ? { backgroundColor: hexToRgba(s.accent, 0.08), color: s.accent } : {}}>
                                    <span className="block font-semibold">{plan.name}</span>
                                    <span className="block text-xs opacity-60 mt-0.5">{plan.currency || 'S/'} {isAnnual && plan.price_annual ? plan.price_annual : plan.price} {plan.period}</span>
                                </button>
                            ))}
                        </div>
                        {/* Right detail panel */}
                        <div className={`rounded-2xl p-6 sm:p-8 border ${s.dark ? 'bg-white/5 border-white/10' : 'bg-white border-gray-100 shadow-lg'}`}>
                            <div className="flex items-start justify-between mb-6">
                                <div>
                                    <h3 className={`text-2xl font-bold ${s.dark ? 'text-white' : 'text-gray-900'}`}>{selected.name}</h3>
                                    {selected.description && <p className="text-sm mt-1" style={{ color: s.textSub }}>{selected.description}</p>}
                                </div>
                                <div className="text-right">
                                    <span className="text-3xl font-extrabold" style={{ color: s.accent }}>{selected.currency || 'S/'} {isAnnual && selected.price_annual ? selected.price_annual : selected.price}</span>
                                    {selected.period && <span className="block text-sm" style={{ color: s.textSub }}>{selected.period}</span>}
                                </div>
                            </div>
                            <FeatureList plan={selected} checkColor={s.accent} />
                            {selected.cta_text && <a {...resolveLinkProps(selected.cta_link || '#contact')} className="inline-flex items-center px-8 py-3 rounded-xl text-sm font-semibold text-white hover:opacity-90 transition-all mt-4" style={{ backgroundColor: s.accent }}>{selected.cta_text}</a>}
                        </div>
                    </div>
                </div>
                <SectionDivider type={config.divider_bottom} color={config.divider_bottom_color || '#ffffff'} position="bottom" config={config}/>
            </section>
        );
    }

    // ── Sticky Bands variant ──
    if (variant === 'sticky-bands') {
        const allFeatures = [...new Set(items.flatMap(p => (Array.isArray(p.features) ? p.features : (p.features || '').split('\n').filter(f => f.trim()))))];
        return (
            <section className={cls} style={bgS}>
                <SectionDivider type={config.divider_top} color="transparent" position="top" config={config}/>
                <div ref={ref} className={`${s.maxWidth} mx-auto px-4 sm:px-6 lg:px-8 ${anim}`}>
                    <SectionHeader tag={content.tag} title={content.title} subtitle={content.subtitle} config={config} s={s} />
                    <Toggle />
                    <div className="mt-8 overflow-x-auto">
                        <table className="w-full min-w-[600px]">
                            <thead className="sticky top-0 z-10">
                                <tr style={{ backgroundColor: s.dark ? '#111827' : '#fff' }}>
                                    <th className={`text-left p-4 text-sm font-medium ${s.dark ? 'text-white/50' : 'text-gray-400'}`}>Caracteristicas</th>
                                    {items.map((plan, i) => (
                                        <th key={i} className="p-4 text-center">
                                            <span className={`block text-base font-bold ${s.dark ? 'text-white' : 'text-gray-900'}`}>{plan.name}</span>
                                            <span className="block text-xl font-extrabold mt-1" style={{ color: s.accent }}>{plan.currency || 'S/'} {isAnnual && plan.price_annual ? plan.price_annual : plan.price}</span>
                                            <span className="block text-xs" style={{ color: s.textSub }}>{plan.period}</span>
                                            {plan.cta_text && <a {...resolveLinkProps(plan.cta_link || '#contact')} className="inline-block mt-2 px-4 py-1.5 rounded-lg text-xs font-semibold text-white hover:opacity-90 transition-opacity" style={{ backgroundColor: plan.popular ? s.accent : (s.dark ? 'rgba(255,255,255,0.15)' : '#6b7280') }}>{plan.cta_text}</a>}
                                        </th>
                                    ))}
                                </tr>
                            </thead>
                            <tbody>
                                {allFeatures.map((feat, fi) => (
                                    <tr key={fi} className={fi % 2 === 0 ? (s.dark ? 'bg-white/[0.02]' : 'bg-gray-50/50') : ''}>
                                        <td className={`p-3 text-sm ${s.dark ? 'text-white/70' : 'text-gray-600'}`}>{feat.trim()}</td>
                                        {items.map((plan, pi) => {
                                            const planFeats = Array.isArray(plan.features) ? plan.features : (plan.features || '').split('\n').map(f => f.trim());
                                            const has = planFeats.some(f => f.trim() === feat.trim());
                                            return <td key={pi} className="p-3 text-center">{has ? <svg className="w-5 h-5 mx-auto" style={{ color: s.accent }} fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M5 13l4 4L19 7"/></svg> : <span className="text-gray-300">—</span>}</td>;
                                        })}
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                </div>
                <SectionDivider type={config.divider_bottom} color={config.divider_bottom_color || '#ffffff'} position="bottom" config={config}/>
            </section>
        );
    }

    // ── Interactive Calculator variant ──
    if (variant === 'interactive-calculator') {
        const [quantity, setQuantity] = useState(1);
        const unit = config.calculator_unit || 'usuarios';
        return (
            <section className={cls} style={bgS}>
                <SectionDivider type={config.divider_top} color="transparent" position="top" config={config}/>
                <div ref={ref} className={`${s.maxWidth} mx-auto px-4 sm:px-6 lg:px-8 ${anim}`}>
                    <SectionHeader tag={content.tag} title={content.title} subtitle={content.subtitle} config={config} s={s} />

                    {/* Quantity slider */}
                    <div className={`max-w-md mx-auto mb-10 transition-all duration-500 ${visible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-4'}`}>
                        <div className="flex items-center justify-between mb-3">
                            <span className="text-sm font-medium" style={{ color: s.text }}>Cantidad de {unit}</span>
                            <span className="text-2xl font-extrabold tabular-nums" style={{ color: s.accent }}>{quantity}</span>
                        </div>
                        <input
                            type="range"
                            min={1}
                            max={100}
                            value={quantity}
                            onChange={e => setQuantity(Number(e.target.value))}
                            className="w-full h-2 rounded-full appearance-none cursor-pointer"
                            style={{ accentColor: s.accent, background: `linear-gradient(to right, ${s.accent} ${quantity}%, ${s.dark ? 'rgba(255,255,255,0.1)' : '#e5e7eb'} ${quantity}%)` }}
                        />
                        <div className="flex justify-between mt-1 text-xs" style={{ color: s.textSub }}>
                            <span>1</span>
                            <span>100</span>
                        </div>
                    </div>

                    <div className={gridCls}>
                        {items.map((plan, i) => {
                            const isPopular = plan.popular;
                            const basePrice = parseFloat(String(plan.price).replace(/[^0-9.]/g, '')) || 0;
                            const totalPrice = (basePrice * quantity).toFixed(2).replace(/\.00$/, '');
                            return (
                                <div key={i} className={`relative rounded-2xl p-6 flex flex-col transition-all duration-500 hover:-translate-y-1 ${visible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-6'} ${
                                    isPopular ? 'shadow-xl border-2' : 'shadow-md border'
                                }`}
                                    style={{
                                        backgroundColor: s.dark ? (isPopular ? 'rgba(255,255,255,0.08)' : 'rgba(255,255,255,0.03)') : '#ffffff',
                                        borderColor: isPopular ? s.accent : (s.dark ? 'rgba(255,255,255,0.1)' : '#e5e7eb'),
                                        transitionDelay: visible ? `${i * 100}ms` : '0ms',
                                    }}>
                                    {isPopular && (
                                        <span className="absolute -top-3 left-1/2 -translate-x-1/2 px-4 py-1 rounded-full text-xs font-bold text-white" style={{ backgroundColor: s.accent }}>
                                            {plan.popular_label || 'Popular'}
                                        </span>
                                    )}
                                    <h3 className="text-lg font-bold" style={{ color: s.text }}>{plan.name}</h3>
                                    {plan.description && <p className="text-sm mt-1" style={{ color: s.textSub }}>{plan.description}</p>}

                                    {/* Per-unit price */}
                                    <div className="mt-4 mb-1">
                                        <span className="text-sm" style={{ color: s.textSub }}>{plan.currency || 'S/'} {plan.price} / {unit.replace(/s$/, '')}</span>
                                    </div>

                                    {/* Total price */}
                                    <div className="mb-6">
                                        <span className="text-4xl font-extrabold transition-all duration-300" style={{ color: isPopular ? s.accent : s.text }}>
                                            {plan.currency || 'S/'} {totalPrice}
                                        </span>
                                        {plan.period && <span className="text-sm ml-1" style={{ color: s.textSub }}>{plan.period}</span>}
                                        <span className="block text-xs mt-1" style={{ color: s.textSub }}>
                                            {quantity} {unit} x {plan.currency || 'S/'} {plan.price}
                                        </span>
                                    </div>

                                    <FeatureList plan={plan} checkColor={s.accent} />
                                    {plan.cta_text && (
                                        <a {...resolveLinkProps(plan.cta_link || '#contact')}
                                            className={`block w-full text-center py-3 rounded-xl font-semibold text-sm transition-all hover:shadow-lg ${isPopular ? 'text-white' : ''}`}
                                            style={isPopular
                                                ? { backgroundColor: s.accent, color: isColorDark(s.accent) ? '#ffffff' : '#111827' }
                                                : { backgroundColor: hexToRgba(s.accent, 0.1), color: s.accent }}>
                                            {plan.cta_text}
                                        </a>
                                    )}
                                </div>
                            );
                        })}
                    </div>
                </div>
                <SectionDivider type={config.divider_bottom} color={config.divider_bottom_color || '#ffffff'} position="bottom" config={config}/>
            </section>
        );
    }

    // ── Feature Matrix variant ──
    if (variant === 'feature-matrix') {
        // Collect all features from the first plan, using "---" as category separators
        const allRows = [];
        const firstPlanFeatures = Array.isArray(items[0]?.features) ? items[0].features : (items[0]?.features || '').split('\n').filter(f => f.trim());
        let currentCategory = null;
        firstPlanFeatures.forEach(f => {
            const label = typeof f === 'string' ? f.trim() : (f.label || f.text || '');
            if (label === '---' || label.startsWith('---')) {
                // Category separator: text after "---" is the category name
                const catName = label.replace(/^-+\s*/, '').trim();
                if (catName) {
                    currentCategory = catName;
                    allRows.push({ type: 'category', label: catName });
                }
            } else if (label) {
                allRows.push({ type: 'feature', label });
            }
        });

        // Also add features from other plans that the first plan doesn't have
        const existingLabels = new Set(allRows.filter(r => r.type === 'feature').map(r => r.label));
        items.slice(1).forEach(plan => {
            const features = Array.isArray(plan.features) ? plan.features : (plan.features || '').split('\n').filter(f => f.trim());
            features.forEach(f => {
                const label = typeof f === 'string' ? f.trim() : (f.label || f.text || '');
                if (label && !label.startsWith('---') && !existingLabels.has(label)) {
                    existingLabels.add(label);
                    allRows.push({ type: 'feature', label });
                }
            });
        });

        // Check if a plan has a feature
        const planHasFeature = (plan, featureLabel) => {
            const features = Array.isArray(plan.features) ? plan.features : (plan.features || '').split('\n').filter(f => f.trim());
            return features.some(f => {
                const label = typeof f === 'string' ? f.trim() : (f.label || f.text || '');
                return label === featureLabel;
            });
        };

        const tableBg = s.dark ? '#0f172a' : (config.bg_color || '#ffffff');

        return (
            <section className={cls} style={bgS}>
                <SectionDivider type={config.divider_top} color="transparent" position="top" config={config}/>
                <div ref={ref} className={`${s.maxWidth} mx-auto px-4 sm:px-6 lg:px-8 ${anim}`}>
                    <SectionHeader tag={content.tag} title={content.title} subtitle={content.subtitle} config={config} s={s} />
                    <Toggle />
                    <div className={`overflow-x-auto -mx-4 sm:mx-0 transition-all duration-500 ${visible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-6'}`}>
                        <table className="w-full min-w-[600px] border-collapse">
                            <thead>
                                <tr className="sticky top-0 z-10" style={{ backgroundColor: tableBg }}>
                                    <th className="text-left p-4 font-semibold text-sm sticky left-0 z-20 min-w-[180px]"
                                        style={{ color: s.textSub, backgroundColor: tableBg }}>
                                        {content.features_label || 'Caracteristicas'}
                                    </th>
                                    {items.map((plan, i) => {
                                        const isPopular = plan.popular;
                                        const displayPrice = isAnnual && plan.price_annual ? plan.price_annual : plan.price;
                                        return (
                                            <th key={i} className={`text-center p-4 min-w-[140px] ${isPopular ? 'relative' : ''}`}
                                                style={isPopular ? { backgroundColor: hexToRgba(s.accent, 0.08) } : { backgroundColor: tableBg }}>
                                                {isPopular && (
                                                    <span className="absolute -top-3 left-1/2 -translate-x-1/2 px-3 py-0.5 rounded-full text-[10px] font-bold text-white whitespace-nowrap" style={{ backgroundColor: s.accent }}>
                                                        {plan.popular_label || 'Recomendado'}
                                                    </span>
                                                )}
                                                <div className="text-base font-bold" style={{ color: s.text }}>{plan.name}</div>
                                                <div className="text-2xl font-extrabold mt-1" style={{ color: isPopular ? s.accent : s.text }}>
                                                    {plan.currency || 'S/'} {displayPrice}
                                                </div>
                                                {plan.period && <div className="text-xs mt-0.5" style={{ color: s.textSub }}>{isAnnual && plan.price_annual ? (plan.annual_period || '/anual') : plan.period}</div>}
                                                {plan.cta_text && (
                                                    <a {...resolveLinkProps(plan.cta_link || '#contact')}
                                                        className="inline-block mt-2 px-5 py-2 rounded-lg text-xs font-semibold transition-all hover:opacity-90"
                                                        style={isPopular
                                                            ? { backgroundColor: s.accent, color: isColorDark(s.accent) ? '#ffffff' : '#111827' }
                                                            : { border: `1.5px solid ${s.accent}`, color: s.accent }}>
                                                        {plan.cta_text}
                                                    </a>
                                                )}
                                            </th>
                                        );
                                    })}
                                </tr>
                            </thead>
                            <tbody>
                                {allRows.map((row, ri) => {
                                    if (row.type === 'category') {
                                        return (
                                            <tr key={ri}>
                                                <td colSpan={items.length + 1} className="p-4 pt-6 text-xs font-bold uppercase tracking-wider"
                                                    style={{ color: s.accent, borderBottom: `2px solid ${hexToRgba(s.accent, 0.2)}` }}>
                                                    {row.label}
                                                </td>
                                            </tr>
                                        );
                                    }
                                    return (
                                        <tr key={ri} className="transition-colors" style={{ borderBottom: `1px solid ${s.dark ? 'rgba(255,255,255,0.06)' : '#f3f4f6'}` }}>
                                            <td className="p-4 text-sm font-medium sticky left-0 min-w-[180px]"
                                                style={{ color: s.text, backgroundColor: tableBg }}>
                                                {row.label}
                                            </td>
                                            {items.map((plan, pi) => {
                                                const has = planHasFeature(plan, row.label);
                                                const isPopular = plan.popular;
                                                return (
                                                    <td key={pi} className="p-4 text-center"
                                                        style={isPopular ? { backgroundColor: hexToRgba(s.accent, 0.04) } : {}}>
                                                        {has ? (
                                                            <svg className="w-5 h-5 mx-auto" style={{ color: s.accent }} fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M5 13l4 4L19 7"/>
                                                            </svg>
                                                        ) : (
                                                            <svg className="w-5 h-5 mx-auto opacity-30" style={{ color: s.dark ? 'rgba(255,255,255,0.3)' : '#9ca3af' }} fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12"/>
                                                            </svg>
                                                        )}
                                                    </td>
                                                );
                                            })}
                                        </tr>
                                    );
                                })}
                            </tbody>
                        </table>
                    </div>
                </div>
                <SectionDivider type={config.divider_bottom} color={config.divider_bottom_color || '#ffffff'} position="bottom" config={config}/>
            </section>
        );
    }

    // ── Unified Features variant ──
    // All plans share the same feature list; differentiation is only by scale/limits
    if (variant === 'unified-features') {
        const sharedFeatures = Array.isArray(content.shared_features)
            ? content.shared_features
            : (content.shared_features || '').split('\n').filter(f => f.trim());
        return (
            <section className={cls} style={bgS}>
                <SectionDivider type={config.divider_top} color="transparent" position="top" config={config}/>
                <div ref={ref} className={`${s.maxWidth} mx-auto px-4 sm:px-6 lg:px-8 ${anim}`}>
                    <SectionHeader tag={content.tag} title={content.title} subtitle={content.subtitle} config={config} s={s} />
                    <Toggle />
                    <div className={gridCls}>
                        {items.map((plan, i) => {
                            const isPopular = plan.popular;
                            return (
                                <div key={i} className={`relative ${getCardRadius(config) || 'rounded-2xl'} ${getCardPadding(config) || 'p-6'} flex flex-col transition-all duration-500 hover:-translate-y-1 ${visible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-6'} ${
                                    isPopular ? 'border-2 shadow-xl' : 'border shadow-sm'
                                }`}
                                    style={{
                                        backgroundColor: s.dark ? (isPopular ? 'rgba(255,255,255,0.06)' : 'rgba(255,255,255,0.03)') : '#ffffff',
                                        borderColor: isPopular ? s.accent : (s.dark ? 'rgba(255,255,255,0.1)' : '#e5e7eb'),
                                        transitionDelay: visible ? `${i * 100}ms` : '0ms',
                                    }}>
                                    {isPopular && (
                                        <span className="absolute -top-3 left-1/2 -translate-x-1/2 px-4 py-1 rounded-full text-xs font-bold text-white" style={{ backgroundColor: s.accent }}>
                                            {plan.popular_label || 'Mas popular'}
                                        </span>
                                    )}
                                    <h3 className="text-lg font-bold" style={{ color: s.text }}>{plan.name}</h3>
                                    {plan.description && <p className="text-sm mt-1" style={{ color: s.textSub }}>{plan.description}</p>}
                                    <PriceDisplay plan={plan} colorStyle={{ color: isPopular ? s.accent : s.text }} />
                                    {/* Per-plan limits/differentiators */}
                                    <FeatureList plan={plan} checkColor={s.accent} />
                                    {plan.cta_text && (
                                        <a {...resolveLinkProps(plan.cta_link || '#contact')}
                                            className={`block w-full text-center py-3 ${getCardRadius(config) || 'rounded-xl'} font-semibold text-sm transition-all hover:opacity-90 mt-auto`}
                                            style={isPopular
                                                ? { backgroundColor: plan.button_color || s.accent, color: isColorDark(plan.button_color || s.accent) ? '#ffffff' : '#111827' }
                                                : { backgroundColor: s.dark ? 'rgba(255,255,255,0.08)' : '#f3f4f6', color: s.text }}>
                                            {plan.cta_text}
                                        </a>
                                    )}
                                </div>
                            );
                        })}
                    </div>
                    {/* Shared feature list below all cards */}
                    {sharedFeatures.length > 0 && (
                        <div className={`mt-10 max-w-3xl mx-auto transition-all duration-700 ${visible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-6'}`}
                            style={{ transitionDelay: '400ms' }}>
                            <h4 className="text-center text-sm font-bold uppercase tracking-wider mb-6" style={{ color: s.textSub }}>
                                {content.shared_features_title || 'Incluido en todos los planes'}
                            </h4>
                            <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-8 gap-y-3">
                                {sharedFeatures.map((f, fi) => (
                                    <div key={fi} className="flex items-center gap-2.5">
                                        <svg className="w-4.5 h-4.5 shrink-0" style={{ color: s.accent }} fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M5 13l4 4L19 7"/>
                                        </svg>
                                        <span className="text-sm" style={{ color: s.text }}>{typeof f === 'string' ? f.trim() : f}</span>
                                    </div>
                                ))}
                            </div>
                        </div>
                    )}
                </div>
                <SectionDivider type={config.divider_bottom} color={config.divider_bottom_color || '#ffffff'} position="bottom" config={config}/>
            </section>
        );
    }

    // ── Default (cards) variant ──
    return (
        <section className={cls} style={bgS}>
            <SectionDivider type={config.divider_top} color="transparent" position="top"  config={config}/>
            <div ref={ref} className={`${s.maxWidth} mx-auto px-4 sm:px-6 lg:px-8 ${anim}`}>
                <SectionHeader tag={content.tag} title={content.title} subtitle={content.subtitle} config={config} s={s} />

                {/* Monthly / Annual toggle */}
                {showToggle && (
                    <div className={`flex items-center justify-center gap-3 mb-8 transition-all duration-500 ${visible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-4'}`}>
                        <div className="inline-flex items-center rounded-full p-1" style={{ backgroundColor: s.dark ? 'rgba(255,255,255,0.1)' : '#f3f4f6' }}>
                            <button
                                onClick={() => setIsAnnual(false)}
                                className={`px-5 py-2 rounded-full text-sm font-semibold transition-all duration-300 ${
                                    !isAnnual ? 'text-white shadow-md' : s.dark ? 'text-white/60 hover:text-white/80' : ''
                                }`}
                                style={!isAnnual ? { backgroundColor: s.accent } : { color: s.textSub }}
                            >
                                {content.monthly_label || 'Mensual'}
                            </button>
                            <button
                                onClick={() => setIsAnnual(true)}
                                className={`px-5 py-2 rounded-full text-sm font-semibold transition-all duration-300 ${
                                    isAnnual ? 'text-white shadow-md' : s.dark ? 'text-white/60 hover:text-white/80' : ''
                                }`}
                                style={isAnnual ? { backgroundColor: s.accent } : { color: s.textSub }}
                            >
                                {content.annual_label || 'Anual'}
                            </button>
                        </div>
                        {isAnnual && discountLabel && (
                            <span className="inline-flex items-center gap-1 px-3 py-1 rounded-full text-xs font-bold text-white animate-pulse" style={{ backgroundColor: s.accent }}>
                                {discountLabel}
                            </span>
                        )}
                    </div>
                )}

                <div ref={gsapRef} className={gridCls}>
                    {items.map((plan, i) => {
                        const isPopular = plan.popular;
                        const features = Array.isArray(plan.features) ? plan.features : (plan.features || '').split('\n').filter(f => f.trim());
                        const displayPrice = isAnnual && plan.price_annual ? plan.price_annual : plan.price;
                        const priceChanged = isAnnual && plan.price_annual && plan.price_annual !== plan.price;
                        return (
                            <div key={i} className={`relative ${getCardRadius(config) || 'rounded-2xl'} p-1 transition-all duration-500 hover:-translate-y-1 ${visible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-6'} ${
                                isPopular ? 'shadow-xl scale-[1.02]' : 'shadow-sm'
                            }`}
                                style={{
                                    background: isPopular ? `linear-gradient(135deg, ${s.accent}, ${hexToRgba(s.accent, 0.8)})` : undefined,
                                    transitionDelay: visible ? `${i * 100}ms` : '0ms',
                                }}>
                                {isPopular && (
                                    <div className="absolute -top-3 left-1/2 -translate-x-1/2 px-4 py-1 rounded-full text-xs font-bold text-white" style={{ backgroundColor: s.accent }}>
                                        {plan.popular_label || 'Popular'}
                                    </div>
                                )}
                                <div className={`rounded-xl ${getCardPadding(config) || 'p-6'} h-full flex flex-col ${isPopular ? '' : 'border'}`}
                                    style={{
                                        backgroundColor: s.dark ? (isPopular ? 'rgba(255,255,255,0.1)' : 'rgba(255,255,255,0.05)') : '#ffffff',
                                        borderColor: isPopular ? 'transparent' : (s.dark ? 'rgba(255,255,255,0.1)' : '#f3f4f6'),
                                    }}>
                                    <h3 className="text-lg font-bold" style={{ color: s.text }}>{plan.name}</h3>
                                    <div className="mt-4 mb-6">
                                        {plan.original_price && plan.original_price !== displayPrice && (
                                            <span className="text-lg line-through opacity-40 mr-2">{plan.currency || 'S/'} {plan.original_price}</span>
                                        )}
                                        <span
                                            className="text-4xl font-extrabold transition-all duration-500"
                                            style={{
                                                color: isPopular ? s.accent : s.text,
                                                transform: priceChanged ? 'scale(1.05)' : 'scale(1)',
                                                display: 'inline-block',
                                            }}
                                        >
                                            {plan.currency || 'S/'} {displayPrice}
                                        </span>
                                        {plan.period && <span className="text-sm ml-1" style={{ color: s.textSub }}>{isAnnual && plan.price_annual ? (plan.annual_period || '/anual') : plan.period}</span>}
                                        {priceChanged && plan.discount_label && (
                                            <span className="ml-2 inline-flex items-center px-2 py-0.5 rounded-full text-[10px] font-bold text-white" style={{ backgroundColor: s.accent }}>
                                                {plan.discount_label}
                                            </span>
                                        )}
                                    </div>
                                    {features.length > 0 && (
                                        <ul className="space-y-3 flex-1 mb-6">
                                            {features.map((f, fi) => (
                                                <li key={fi} className="flex items-start gap-2 text-sm" style={{ color: s.textSub }}>
                                                    <svg className="w-4 h-4 shrink-0 mt-0.5" style={{ color: s.accent }} fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M5 13l4 4L19 7"/>
                                                    </svg>
                                                    {f.trim()}
                                                </li>
                                            ))}
                                        </ul>
                                    )}
                                    {plan.cta_text && (
                                        <a {...resolveLinkProps(plan.cta_link || '#contact')}
                                            className={`block w-full text-center py-3 rounded-xl font-semibold text-sm transition-all hover:shadow-lg ${
                                                isPopular ? 'text-white' : 'border-2'
                                            }`}
                                            style={isPopular
                                                ? { backgroundColor: plan.button_color || s.accent }
                                                : { borderColor: plan.button_color || s.accent, color: plan.button_color || s.accent }}>
                                            {plan.cta_text}
                                        </a>
                                    )}
                                </div>
                            </div>
                        );
                    })}
                </div>
            </div>
            <SectionDivider type={config.divider_bottom} color={config.divider_bottom_color || '#ffffff'} position="bottom"  config={config}/>
        </section>
    );
}
